/* eslint-disable camelcase */
import { Grid, IconButton, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { DeleteRounded, SaveRounded } from '@mui/icons-material';
import PropTypes from 'prop-types';
import styles from '../styles';
import columns from './table_config';
import { useDispatch } from 'react-redux';
import { generateRandom } from '../../../../../utils/randomNumberGenerator';
import { enqueueSnackbar } from '../../../../../store/actions/snackbar';
import { COLORS } from '../../../../../shared/config/colors';
import DeleteConfirmationModal from '../../../../deleteConfirmationModal';
import { MAX_ROW_LIMITS } from '../../../../../utils/constants';
import MobileSelectEditValueTypes from '../../SelectEditValueTypes';
import MobileSelectEditInputTextField from '../../SelectEditInputTextField';
import MobileTableInputs from '../tableInputs';

function MobileRunsECDSpan({ units, currentUnit, runsECDSpanNames, loading, targetData, setTargetData, setParameterDirty, setTargetSaveEnable, setRunsEcdDirty, disableTable }) {
  const classes = styles();
  const dispatch = useDispatch();
  const [properties, setProperties] = useState([]);
  const [invalidRows, setInvalidRows] = useState({});
  const [deleteAction, setDeleteAction] = useState(false);
  const [deleteRow, setDeleteRow] = useState();
  const [scrollToNew, setScrollToNew] = useState(false);

  useEffect(() => {
    if (properties.length === 0 && targetData?.ecd_span) {
      const updatedData = targetData?.ecd_span?.map((item) => ({
        ...item,
        _id: generateRandom()
      }));
      setProperties(updatedData);
    }
  }, []);

  const handleAddECDSpan = () => {
    const nextId = properties.length;
    const newRow = {
      row: properties.length + 1,
      name: '',
      low_emw: '',
      high_emw: '',
      grid_id: nextId + 1,
      _id: generateRandom(),
      isNewRow: true,
      isDirty: true,
    };
    setProperties((prev) => [newRow, ...prev]);
    setRunsEcdDirty(true);
  };

  const updateData = (id, field, value) => {
    setProperties(prevProperties => {
      const updatedProperties = prevProperties.map(item => {
        if (item._id === id) {
          const isDirty = value !== item[field];
          return {
            ...item,
            [field]: typeof value === 'string' ? value : Number(value),
            isDirty
          };
        }
        return item;
      });
      return updatedProperties;
    });
    setRunsEcdDirty(true);
  };

  const checkRowValues = (value) => {
    const row = value;
    const invalidFields = [];
    if (row?.name === undefined || row?.name === null || row?.name === '') invalidFields.push('Name');
    if (row?.low_emw === undefined || row?.low_emw === null || row?.low_emw === '') invalidFields.push('Low EMW');
    if (row?.high_emw === undefined || row?.high_emw === null || row?.high_emw === '') invalidFields.push('High EMW');
    if (invalidFields.length === 0) {
      setInvalidRows(prev => ({ ...prev, [row._id]: false }));
      return true;
    } else {
      setInvalidRows(prev => ({ ...prev, [row._id]: true }));
      dispatch(
        enqueueSnackbar(
            `Please enter the following details: ${invalidFields.join(', ')}`,
            'error',
            new Date().getTime() + Math.random()
        )
      );
      return false;
    }
  };

  const validateProperties = (row) => {
    const { low_emw, high_emw, _id } = row;
    if (Number(low_emw) <= units?.low_emw?.min || Number(low_emw) > units?.low_emw?.max) {
      setInvalidRows(prev => ({ ...prev, [_id]: true }));
      dispatch(
        enqueueSnackbar(
          `Low EMW should be between ${units?.low_emw?.min} and ${units?.low_emw?.max}.`,
          'error',
          new Date().getTime() + Math.random()
        )
      );
      return false;
    }

    if (Number(high_emw) <= units?.high_emw?.min || Number(high_emw) > units?.high_emw?.max) {
      setInvalidRows(prev => ({ ...prev, [_id]: true }));
      dispatch(
        enqueueSnackbar(
          `High EMW should be between ${units?.high_emw?.min} and ${units?.high_emw?.max}.`,
          'error',
          new Date().getTime() + Math.random()
        )
      );
      return false;
    }

    if (Number(high_emw) <= Number(low_emw)) {
      setInvalidRows(prev => ({ ...prev, [_id]: true }));
      dispatch(
        enqueueSnackbar(
          'High EMW should be greater than Low EMW value.',
          'error',
          new Date().getTime() + Math.random()
        )
      );
      return false;
    }
    return true;
  };

  function checkForDuplicates(properties) {
    const seen = new Set();

    for (let i = 0; i < properties.length; i++) {
      const { name } = properties[i];
      const key = `${name}`;

      if (seen.has(key)) {
        setInvalidRows(prev => ({ ...prev, [properties[i]?._id]: true }));
        dispatch(
          enqueueSnackbar(
            'Row with name already exists',
            'error',
            new Date().getTime() + Math.random()
          )
        );
        return false;
      }
      seen.add(key);
    }
    return true;
  }

  function getAvailableNames(tableData, dropdownOptions) {
    const selectedNames = tableData
      .map(row => row.name)
      .filter(name => !!name);

    return dropdownOptions.filter(option => !selectedNames.includes(option));
  }

  const handleSave = (row) => {
    if (checkRowValues(row)) {
      if (validateProperties(row) && checkForDuplicates(properties)) {
        setInvalidRows(prev => ({ ...prev, [row._id]: false }));
        return true;
      }
    }
  };

  const markRowAsSaved = (id) => {
    let updated = [];
    setProperties((prevProperties) => {
      updated = prevProperties.map((item) => {
        if (item._id === id) {
          // eslint-disable-next-line no-unused-vars
          const { isDirty, isNewRow, ...rest } = item;
          return { ...rest };
        }
        return item;
      });
      return updated;
    });
    setTimeout(() => {
      setTargetData(prev => ({
        ...prev,
        ecd_span: updated
      }));
      setRunsEcdDirty(false);
      setTargetSaveEnable(true);
    }, 0);

    setParameterDirty(prev => {
      if (!Array.isArray(prev) || prev.length === 0) return prev;
      const updatedDirty = [...prev];
      updatedDirty[0] = { ...updatedDirty[0], isDirty: true };
      return updatedDirty;
    });
  };

  const removeRunsECDSpan = (row) => {
    const updated = properties.filter(item => item._id !== row?._id);
    setProperties(updated);
    const anyDirty = updated.some(item => item?.isDirty);
    if (!anyDirty) {
      setRunsEcdDirty(false);
    }
    setParameterDirty(prev => {
      if (!Array.isArray(prev) || prev.length === 0) return prev;
      const updatedDirty = [...prev];
      updatedDirty[0] = { ...updatedDirty[0], isDirty: true };
      return updatedDirty;
    });
    setTargetData(prev => ({
      ...prev,
      ecd_span: updated
    }));
    setTargetSaveEnable(true);
    setDeleteAction(false);
    setDeleteRow({});
  };

  const renderValueCell = (params) => {
    return (
            <MobileSelectEditInputTextField numberFormatOnly {...params} updateData={updateData} loading={false} />
    );
  };

  const renderNameCell = (params) => {
    return (
          <MobileSelectEditValueTypes
            {...params}
            closeCellAfterSelect={true}
            updateData={updateData}
            data={runsECDSpanNames}
            loading={false}
          />
    );
  };

  const renderActionCell = ({ row }) => {
    return (
      <Grid container justifyContent="center">
        <IconButton
          variant="solid"
          disabled={!row.isDirty || loading}
          onClick={() => {
            if (handleSave(row)) {
              setTimeout(() => {
                markRowAsSaved(row._id);
              }, 100);
            }
          }}
        >
          <SaveRounded />
        </IconButton>
        <IconButton
          variant="solid"
          disabled={loading}
          onClick={() => {
            if (targetData?.ecd_span?.length === 1 && targetData?.controls_table?.length) {
              dispatch(
                enqueueSnackbar(
                  'EMW limits cannot be removed while the Controls Table has entries. Please clear the Controls Table first.',
                  'error',
                  new Date().getTime() + Math.random()
                )
              );
              return;
            }
            setDeleteAction(true);
            setDeleteRow(row);
          }}
        >
          <DeleteRounded />
        </IconButton>
      </Grid>
    );
  };

  return (
    <Grid container maxWidth='1150px'>
        <Grid container item marginLeft='12px' marginTop='5px' marginBottom='5px'>
          <Grid>
            <Typography sx={{ fontSize: '18px', fontWeight: 'bold' }}>Run&apos;s EMW Limits</Typography>
          </Grid>
        </Grid>
        <Grid container>
          <Grid item marginLeft='12px' className={classes.customTool}>
            <Typography variant='button' fontWeight='700' onClick={handleAddECDSpan} className={loading || getAvailableNames(properties, runsECDSpanNames).length === 0 || properties?.some(item => item.isDirty === true) || properties?.length === MAX_ROW_LIMITS.RUNS_ECD_SPAN || disableTable ? classes.disableIcon : ''}>+ Add</Typography>
          </Grid>
        <Grid item marginLeft='12px' className={classes.entryLabel}>
          <Typography sx={{ fontSize: '15px' }} color={COLORS.LIGHT_GREY} fontWeight='500'>{`(Note: Maximum ${MAX_ROW_LIMITS.RUNS_ECD_SPAN} entries are allowed)`}</Typography>
        </Grid>
        </Grid>
        <Grid item xs={12}>
           <MobileTableInputs
              data={properties}
              columns={columns(renderNameCell, renderValueCell, renderActionCell, units, currentUnit)}
              updateData={updateData}
              loading={loading}
              scrollToNew={scrollToNew}
              onScrolled={() => setScrollToNew(false)}
              invalidRows={invalidRows}
              disableTable={disableTable}
            />
        </Grid>
          {
              deleteAction && <DeleteConfirmationModal
                  open={deleteAction}
                  handleDelete={removeRunsECDSpan}
                  handleClose={setDeleteAction}
                  deleteRow={deleteRow}
              />
          }
      </Grid>
  );
}

MobileRunsECDSpan.propTypes = {
  units: PropTypes.object,
  currentUnit: PropTypes.string,
  runsECDSpanNames: PropTypes.array,
  loading: PropTypes.bool,
  targetData: PropTypes.object,
  setTargetData: PropTypes.func,
  setParameterDirty: PropTypes.func,
  setTargetSaveEnable: PropTypes.func,
  setRunsEcdDirty: PropTypes.func,
  disableTable: PropTypes.bool
};

export default MobileRunsECDSpan;
